import { CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { surgicalKitsApi } from "@/api/endpoints";

/**
 * Surgical kit (OT BOM) selector. A hospital keeps a few dozen kits at most, so the whole active
 * list is offered rather than a search box — theatre staff recognise a kit by name.
 *
 * Inactive kits are hidden except one already selected, so an existing record keeps showing the
 * kit it was raised from.
 */
export function SurgicalKitPicker({
  value, onChange, label = "Surgical kit", required, disabled, helperText, sx,
}: {
  value: string;
  onChange: (surgicalKitId: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  sx?: object;
}) {
  const q = useQuery({
    queryKey: ["surgical-kits-picker"],
    queryFn: () => surgicalKitsApi.list({ pageSize: 200 }),
    staleTime: 5 * 60_000,
  });

  const options = (q.data?.items ?? []).filter(k => k.isActive || k.id === value);

  return (
    <TextField
      select size="small" sx={sx} disabled={disabled || q.isLoading}
      label={`${label}${required ? " *" : ""}`}
      value={options.some(k => k.id === value) ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      helperText={helperText ?? (q.isError ? "Could not load surgical kits" : undefined)}
      InputProps={q.isLoading ? { endAdornment: <CircularProgress size={16} /> } : undefined}
    >
      <MenuItem value=""><em>— no kit —</em></MenuItem>
      {options.map(k => (
        <MenuItem key={k.id} value={k.id}>
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="body2">{k.code} — {k.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {k.itemCount} item{k.itemCount === 1 ? "" : "s"}
              {k.procedureCode ? ` · ${k.procedureCode}` : ""}
              {k.isActive ? "" : " · inactive"}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
}
